import clsx from "clsx";
import { CalendarRange, User, Route } from "lucide-react";
import React from "react";
import { useTranslation } from "react-i18next";

import type { WatchlistScope } from "../../watchlist/types";
import type { FilterOption } from "../FilterMultiSelect";

import { ScopeChip, coversEveryone } from "./ScopeChips";

type ScopeSummaryProps = {
  scope: WatchlistScope;
  userOptions: FilterOption[];
  processOptions: FilterOption[];
  className?: string;
};

/**
 * A saved scope written out to be read: whose work the alert watches, which
 * processes it looks at, and how far back every run reaches.
 *
 * The same chips the composer shows while a scope is being built, so an alert
 * is recognisable afterwards as the thing that was set up — plus the two parts
 * the composer keeps in pills, which have no pills to sit in once the alert is
 * saved. "Everybody" and "every process" are spelled out rather than left to
 * an absence of chips: a scope with nothing shown reads as a scope nobody set.
 */
const ScopeSummary = ({ scope, userOptions, processOptions, className }: ScopeSummaryProps) => {
  const { t } = useTranslation();

  const people = userOptions.filter(option => scope.userIds.includes(option.id));
  const processes = processOptions.filter(option => scope.processSlugs.includes(option.id));
  // Empty and "all of them" are the same scope, and the second is what it means.
  // Guarded on there being options at all: while they are still loading every
  // selection matches none of them, which is not the same as watching them all.
  const allProcesses =
    (scope.processSlugs.length === 0 && scope.allProcesses !== false) ||
    (processOptions.length > 0 && processes.length === processOptions.length);

  return (
    <div className={clsx("flex flex-wrap items-center gap-1.5", className)}>
      {scope.userIds.length === 0 || coversEveryone(scope, userOptions) ? (
        <ScopeChip tone="meta" icon={<User size={12} />} label={t("watchlist.all_people")} />
      ) : (
        people.map(person => (
          <ScopeChip key={person.id} tone="person" icon={<User size={12} />} label={person.label} />
        ))
      )}

      {allProcesses ? (
        <ScopeChip tone="meta" icon={<Route size={12} />} label={t("watchlist.all_processes")} />
      ) : (
        processes.map(process => (
          <ScopeChip
            key={process.id}
            tone="process"
            icon={<Route size={12} />}
            label={process.label}
          />
        ))
      )}

      <ScopeChip
        tone="meta"
        icon={<CalendarRange size={12} />}
        label={t("watchlist.window_last_n_days", { count: scope.window.days })}
      />
    </div>
  );
};

export default ScopeSummary;
